import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  ArrowRightIcon,
  CheckIcon,
  CloseSVGIcon,
  LockIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { useFreelancerProfile } from "@js/apps/freelancer/hooks";
import { openChoosePayoutMethodModal } from "@js/apps/withdrawal/components/choose-payout-method-modal";
import { RouterLink } from "@js/components/link";
import { ModalInstance } from "@js/components/modal";

import { useGetIsFreelancerLocationSupported } from "../../hooks/use-get-is-freelancer-location-supported";

import styles from "./styles.module.scss";

export const SkipBankingInfoSetupModalContent = () => {
  const { isFreelancerLocationSupported, isLoading } =
    useGetIsFreelancerLocationSupported();

  if (isLoading) {
    return <></>;
  }

  const onAddInfo = () => {
    ModalInstance.close();
    openChoosePayoutMethodModal();
  };

  if (isFreelancerLocationSupported) {
    return <FreelancerLocationSupportedContent />;
  }

  return <FreelancerLocationUnsupportedContent onAddInfo={onAddInfo} />;
};

const FreelancerLocationSupportedContent = () => {
  const profile = useFreelancerProfile();

  const handleClose = () => {
    ModalInstance.close();
  };

  return (
    <Box>
      <Stack
        className={styles.header}
        sx={{
          p: { xs: 3, sm: 4, md: 5 },
          background: "linear-gradient(148.35deg, #eef7d4 0%, #d2ea9f 100%)",
        }}
      >
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            zIndex: 1,
            backgroundColor: "#fff",
            borderRadius: "1rem",
            width: "56px",
            height: "56px",
            mb: 2.25,
            boxShadow: "0px 4px 12px 0px #14141714",
          }}
        >
          <CheckIcon sx={{ color: "#3a7d44", fontSize: 26 }} />
        </Box>

        <Box
          sx={{
            display: "flex",
            backgroundColor: "#FFFFFFA6",
            borderRadius: "50px",
            padding: "4px 16px",
            mb: 3,
            border: "1px solid #1E75482E",
          }}
        >
          <Typography
            component="span"
            variant="label"
            size="small"
            className="dot-prefix"
            fontWeight={500}
            sx={{
              fontSize: "11px !important",
              letterSpacing: "1.2px !important",
              textTransform: "uppercase",
              color: "#1E7548",
              "--dot-size": "5px",
            }}
          >
            You're all set
          </Typography>
        </Box>

        <Typography
          component="h2"
          variant="title"
          size="small"
          sx={{
            maxWidth: "340px",
            mb: 0,
            fontWeight: 700,
            fontSize: "28px !important",
            lineHeight: "36px !important",
          }}
        >
          You can set up your bank information any time from your profile.
        </Typography>
      </Stack>

      <Stack sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Typography
          component="p"
          variant="paragraph"
          size="medium"
          color="grey-1"
          sx={{
            textAlign: "center",
            mb: 3,
            fontSize: "14px !important",
            maxWidth: "370px",
            marginInline: "auto",
          }}
        >
          You'll be prompted to add your bank information once you accept an
          offer for a job.
        </Typography>

        <Stack direction="row" sx={{ justifyContent: "center", gap: 1.5 }}>
          <Button
            variant="white-violet"
            shape="squared"
            onClick={handleClose}
            sx={{ fontSize: "14px !important" }}
          >
            Close
          </Button>
          <Button
            variant="dark-violet"
            shape="squared"
            RouterLink={RouterLink}
            href={profile?.user.profile_url}
            onClick={handleClose}
            endIcon={<ArrowRightIcon />}
            sx={{ fontSize: "14px !important" }}
          >
            Go to My Profile
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

type FreelancerLocationUnsupportedContentProps = {
  onClose?: () => void;
  onAddInfo: () => void;
};
export const FreelancerLocationUnsupportedContent = ({
  onClose,
  onAddInfo,
}: FreelancerLocationUnsupportedContentProps) => {
  const profile = useFreelancerProfile();
  const hasBid = !!profile?.has_bid;

  const handleAddInfo = () => {
    onAddInfo();
  };

  return (
    <Box sx={{ width: "min(100vw - 2rem, 560px)" }}>
      {!!onClose && (
        <IconButton
          onClick={onClose}
          aria-label="close modal"
          size="small"
          variant="gray-red"
          sx={{
            svg: { m: 0 },
            position: "absolute",
            top: { xs: "14px", sm: "20px" },
            right: { xs: "14px", sm: "20px" },
          }}
        >
          <CloseSVGIcon />
        </IconButton>
      )}
      <Stack
        className={styles.header}
        sx={{
          p: { xs: 3, sm: 4, md: 5 },
          background: "linear-gradient(140.32deg, #FFE9E7 0%, #F8C8C5 100%)",
        }}
      >
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            zIndex: 1,
            backgroundColor: "#fff",
            borderRadius: "1rem",
            width: "56px",
            height: "56px",
            mb: 2.25,
            boxShadow: "0px 4px 12px 0px #14141714",
          }}
        >
          <LockIcon sx={{ color: "#B04D4A", fontSize: 26 }} />
        </Box>

        <Box
          sx={{
            display: "flex",
            backgroundColor: "#FFFFFFA6",
            borderRadius: "50px",
            padding: "4px 16px",
            mb: 3,
            border: "1px solid #B04D4A2E",
          }}
        >
          <Typography
            component="span"
            variant="label"
            size="small"
            className="dot-prefix"
            fontWeight={500}
            sx={{
              fontSize: "11px !important",
              letterSpacing: "1.2px !important",
              textTransform: "uppercase",
              color: "#B04D4A",
              "--dot-size": "5px",
            }}
          >
            Banking required
          </Typography>
        </Box>

        <Typography
          component="h2"
          variant="title"
          size="small"
          fontWeight={500}
          sx={{
            mb: 2,
            fontSize: "28px !important",
            lineHeight: "36px !important",
          }}
        >
          Set up your banking information
        </Typography>
        <Typography
          component="p"
          variant="paragraph"
          size="medium"
          color="grey-1"
          sx={{
            fontSize: "14px !important",
            maxWidth: "260px",
          }}
        >
          {hasBid ? (
            <>
              Your active application can't proceed until we have your bank
              details on file.
            </>
          ) : (
            <>
              We need your bank details on file before you can apply to any
              role.
            </>
          )}
        </Typography>
      </Stack>

      <Stack sx={{ p: { xs: 2, sm: 3, md: 3.75 } }}>
        <Typography
          component="p"
          variant="paragraph"
          size="medium"
          color="grey-1"
          sx={{
            textAlign: "center",
            mb: 3,
            fontSize: "14px !important",
            marginInline: "auto",
          }}
        >
          In order to ensure we can fulfill your invoices in the event you're
          hired, please enter your bank account information.{" "}
          <Typography
            component="span"
            color="grey-1"
            fontWeight={500}
            size="small"
            sx={{ fontSize: "inherit !important" }}
          >
            Without this, you cannot {hasBid ? <>get hired</> : <>apply</>}.
          </Typography>
        </Typography>

        <Stack direction="row" sx={{ justifyContent: "center", gap: 1.5 }}>
          <Button
            variant="dark-violet"
            shape="squared"
            onClick={handleAddInfo}
            endIcon={<ArrowRightIcon />}
            sx={{ fontSize: "14px !important" }}
          >
            Add Banking Information
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export const openSkipBankingInfoSetupModal = () => {
  ModalInstance.open({
    closeButton: true,
    className: styles.modal,
    padding: false,
    closeButtonProps: {
      variant: "transparent",
      sx: { svg: { m: 0 }, color: "var(--gray-1) !important" },
    },
    children: <SkipBankingInfoSetupModalContent />,
  });
};
